import { throttle } from 'throttle-debounce'
import tippy from 'tippy.js'
import 'tippy.js/animations/shift-away-subtle.css'
import { processResponse } from '~/site/behaviors/remote/process_response'
import { isDesktopView, localizedLongDate } from './helpers'
import { luxonTimeFormat } from './time_format'

/*
 * This is the final form of the booking flow.
 * Time slots have already been selected at this point.
 * All they need to do is enter details like name/email and press submit to confirm booking
 * The tricky part is that on desktop this is a popup, where on mobile it's a full-screen element
 */

export class BookingFormManager {
  constructor(formContainer, toLuxonDateTimeWithTz, localeValue) {
    this.originalContainer = formContainer.parentElement
    this.form = formContainer
    this.toLuxonDateTimeWithTz = toLuxonDateTimeWithTz
    this.localeValue = localeValue

    // Hides the booking form if visible when a click is made outside when in desktop view/or escape is pressed
    this.attachHideListeners()

    // On mobile, we want this to be a fullwidth popup. On desktop, we want it to be a popup that's positioned relative to the active slot. To get that behavior, we have to move the element inside/outside of a popup on resize. This stuff isn't strictly necessary, because normal people won't usually resize their browser to go from desktop to mobile but some might do.
    window.addEventListener('resize', throttle(100, this.move))

    this.form.addEventListener('ajax:success', (event) =>
      processResponse(event.detail[0], this.form.querySelector('form'))
    )

    // Re-render the selected slot's time when the visitor toggles 24h.
    document.addEventListener('time-format:change', this.onTimeFormatChange)
  }

  // Remove document-level listeners so a stale manager (left behind after a
  // Turbo/Stimulus reconnect) can't react to later events against detached DOM.
  destroy() {
    document.removeEventListener('time-format:change', this.onTimeFormatChange)
    // The scrim lives on <body>, outside the controller's element, so nothing else removes it.
    this.scrim?.parentElement?.remove()
    this.scrim = null
  }

  onTimeFormatChange = () => {
    if (this.currentSlot && !this.form.classList.contains('hidden')) {
      this.update(this.currentSlot)
    }
  }

  // `slot` is the slot to show the booking form for. an object with `start` and `end` properties that are luxon DateTime objects
  // `type` is either 'normal' or 'tippy' - whether to show the form in a tippy or not
  // `getReferenceEl` is a function that returns the element to position the tippy relative to. Required if `type` is 'tippy'
  show = ({ slot, type, getReferenceEl }) => {
    this.update(slot)
    this.form.classList.remove('hidden')
    // capacity_hold_slot_controller.js holds this time while the form is open.
    document.dispatchEvent(
      new CustomEvent('booking-form:show', {
        detail: {
          startsAt: slot.start.toUTC().toISO(),
          endsAt: slot.end.toUTC().toISO(),
        },
      })
    )

    if (type == 'tippy') {
      if (!this.formTippy) {
        this.formTippy = tippy(getReferenceEl(), {
          content: this.form,
          showOnCreate: true,
          trigger: 'manual',
          appendTo: document.querySelector('.tippy-portal') || document.body,
          zIndex: 110, // above the scrim (z-100)
          placement: 'left',
          maxWidth: 'none',
          animation: 'shift-away-subtle',
          hideOnClick: false,
          interactive: true,
          getReferenceClientRect: () => {
            return getReferenceEl?.()?.getBoundingClientRect()
          },
          popperOptions: {
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['right', 'left', 'top', 'bottom'],
                },
              },
            ],
          },
        })
      } else {
        this.formTippy.show()
        this.formTippy.setProps({})
      }
      this.showScrim()
    }

    this.form.querySelector('input:not([type=hidden])')?.focus()
  }

  hide = () => {
    if (this.formTippy) {
      this.formTippy.hide()
    }
    this.hideScrim()
    this.form.classList.add('hidden')
    document.dispatchEvent(new CustomEvent('booking-form:hide'))
  }

  // Dims the page behind the desktop popup so the form reads as the thing to look at, in both
  // color schemes. A click on it lands outside the popup, which the outside-click listener treats
  // as a cancel.
  showScrim = () => {
    if (!this.scrim) {
      // The `tw` scope must sit on an ancestor, so the utilities (including `hidden`) go on a child.
      const scope = document.createElement('div')
      scope.className = 'tw'
      // Turbo must not snapshot it: a restored page would bring back a scrim no manager can remove.
      scope.dataset.turboTemporary = ''
      this.scrim = document.createElement('div')
      this.scrim.className =
        'fixed inset-0 z-100 bg-black/20 transition-opacity dark:bg-black/40'
      this.scrim.dataset.bookingScrim = ''
      scope.appendChild(this.scrim)
      document.body.appendChild(scope)
    }
    this.scrim.classList.remove('hidden')
  }

  hideScrim = () => {
    this.scrim?.classList.add('hidden')
  }

  /***************
    Private stuff
  ***************/

  move = () => {
    const inTippy = !!this.form.closest('.tippy-box')
    const shouldBeInTippy = isDesktopView()
    const tippyExists = !!this.formTippy
    if (tippyExists && !inTippy && shouldBeInTippy) {
      this.hide()
      this.formTippy = null
    } else if (inTippy && !shouldBeInTippy) {
      // The phone layout is a full-screen step: the desktop scrim would sit on top of it.
      this.hideScrim()
      this.originalContainer.appendChild(this.form)
    }
  }

  attachHideListeners() {
    // Always close the booking form on an escape key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hide()
    })

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-behavior*=hide-booking-form]')) this.hide()
    })

    // Close the booking form if the user clicks outside of it (if the booking form is in a popup, i.e desktop view)
    document.addEventListener('click', (e) => {
      if (
        !e.target.closest('.tippy-box') &&
        !e.target.closest('.fc-event:not(.calendar-unavailable') &&
        !e.target.closest('.iti') &&
        this.form.closest('.tippy-box')
      )
        this.hide()
    })
  }

  update = (slot) => {
    this.currentSlot = slot
    const { start, end } = slot

    // Update inputs
    this.form.querySelector('.booking-starts-at-input').value = start.toUTC()
    this.form.querySelector('.booking-ends-at-input').value = end.toUTC()

    // Render time shown on form (i.e the slot selected currently)
    this.updateTime({
      start,
      end,
      selector: '.new-booking-datetime',
    })

    const oldBookingDatetime = this.form.querySelector('.old-booking-datetime')
    if (oldBookingDatetime) {
      // Render correct time of old (existing) booking (i.e in case the user is rescheduling an existing booking)
      // the value this renders won't normally change, but people can change TZ
      this.updateTime({
        start: this.toLuxonDateTimeWithTz(
          new Date(oldBookingDatetime.dataset.startsAt)
        ),
        end: this.toLuxonDateTimeWithTz(
          new Date(oldBookingDatetime.dataset.endsAt)
        ),
        selector: '.old-booking-datetime',
      })
    }
  }

  updateTime = ({ start, end, selector }) => {
    const startDate = localizedLongDate(start, this.localeValue)
    const startTime = start.toFormat(luxonTimeFormat())
    const endTime = end.toFormat(luxonTimeFormat())
    const tz = start.toFormat('ZZZZ')

    const container = this.form.querySelector(selector)
    container.querySelector('.booking-date').innerText = startDate
    container.querySelector(
      '.booking-time'
    ).innerText = `${startTime} - ${endTime} (${tz})`
  }
}
